import React from 'react';

import { present_parts } from './table_layout';

/*
 * Composed cells of the list-row pattern (§2.3): who · what · money · status+actions, and the note
 * line under a row. Presentational only; styles in responsive_table.css (.tp-c-*, .tp-ledger).
 */

// Empty parts drop out, so a meta row never prints a dangling gap.
const Parts = ({ parts, className }) => {
  const list = present_parts(parts || []);
  if (!list.length) return null;
  return (
    <div className={className}>
      {list.map((p, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i}>{p}</span>
      ))}
    </div>
  );
};

// Row identifier: name (13/600), then a wrapping meta row (mono id · date · "Student <link>").
export const Who = ({ name, sub, meta }) => (
  <div className="tp-c-who">
    <div className="tp-c-name">{name}</div>
    {sub ? <div className="tp-c-sub">{sub}</div> : null}
    <Parts parts={meta} className="tp-c-meta" />
  </div>
);

// What the row is: a title (12/600) and muted lines under it (never orange: they are not links).
export const What = ({ title, lines, children }) => (
  <div className="tp-c-what">
    <div className="tp-c-title">{title}</div>
    {(lines || [])
      .filter((l) => l !== null && l !== undefined && l !== false && l !== '')
      // eslint-disable-next-line react/no-array-index-key
      .map((l, i) => (
        <div key={i} className="tp-c-line">
          {l}
        </div>
      ))}
    {children}
  </div>
);

export const TONE_CLASS = {
  neg: 'tp-ledger-neg',
  pos: 'tp-ledger-pos',
  zero: 'tp-ledger-zero',
  warn: 'tp-ledger-warn'
};
const present = (x) => x && x.value !== null && x.value !== undefined && x.value !== '';

/*
 * Money as a ledger: label/value rows, values right-aligned with tabular figures, the decision
 * number last and bold. A row whose value does not fit beside its label drops it to the next line,
 * still right-aligned. items: [{ label, value, tone? }], total: { label, value, tone? }.
 */
export const Ledger = ({ items, total }) => (
  <dl className="tp-ledger">
    {(items || []).filter(present).map((x) => (
      <div key={x.label} className="tp-ledger-row">
        <dt>{x.label}</dt>
        <dd className={TONE_CLASS[x.tone] || undefined}>{x.value}</dd>
      </div>
    ))}
    {present(total) ? (
      <div
        className={`tp-ledger-row tp-ledger-tot${TONE_CLASS[total.tone] ? ` ${TONE_CLASS[total.tone]}` : ''}`}
      >
        <dt>{total.label}</dt>
        <dd>{total.value}</dd>
      </div>
    ) : null}
  </dl>
);

// Status track: chip, muted meta lines, then the row actions, right-aligned.
export const StatusCell = ({ chip, meta, actions }) => (
  <div className="tp-c-status">
    {chip}
    {(meta || [])
      .filter((m) => m !== null && m !== undefined && m !== false && m !== '')
      // eslint-disable-next-line react/no-array-index-key
      .map((m, i) => (
        <div key={i} className="tp-c-st-meta">
          {m}
        </div>
      ))}
    {actions || null}
  </div>
);

// The note under a row: bold label, text, a muted byline; warn for a state that needs attention.
export const Note = ({ label, text, by, warn = false, children }) => {
  if (!label && !text && !by && !children) return null;
  return (
    <div className={`tp-c-note${warn ? ' tp-c-note-warn' : ''}`}>
      {label ? <b>{label}</b> : null}
      {label && text ? ' · ' : null}
      {text || null}
      {children}
      {by ? <span className="tp-c-by">{by}</span> : null}
    </div>
  );
};

// Signed rupee amounts inside running text ("Net +₹1,500"): split out so each stays on one line.
const AMOUNT_RE = /([-−+]?₹[\d,]+(?:\.\d+)?)/;
export const amount_parts = (text) =>
  String(text === null || text === undefined ? '' : text)
    .split(AMOUNT_RE)
    .filter((p) => p !== '')
    .map((p) => ({ text: p, amount: AMOUNT_RE.test(p) && p.replace(AMOUNT_RE, '') === '' }));

export const NoWrapAmounts = ({ text }) =>
  amount_parts(text).map((p, i) =>
    p.amount ? (
      // eslint-disable-next-line react/no-array-index-key
      <span key={i} className="tp-nowrap">
        {p.text}
      </span>
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={i}>{p.text}</React.Fragment>
    )
  );
